import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import deviceService from '../lib/device.service';
import { deviceKeys } from './use-devices';
import type { DeviceLogFileFilter } from '../types';

export const logFileKeys = {
  files: (deviceId: string, filter?: DeviceLogFileFilter) =>
    [...deviceKeys.detail(deviceId), 'logFiles', filter] as const,
  // Prefix without the trailing `filter` element — use this with
  // invalidateQueries so EVERY filter variant refetches after a sync.
  // Matching against `files(deviceId)` (which ends in `undefined`) would
  // skip any query whose key carries a real filter object.
  filesAll: (deviceId: string) =>
    [...deviceKeys.detail(deviceId), 'logFiles'] as const,
  storageInfo: (deviceId: string) =>
    [...deviceKeys.detail(deviceId), 'logFileStorageInfo'] as const,
  syncStatus: (deviceId: string) =>
    [...deviceKeys.detail(deviceId), 'syncGCSStatus'] as const,
};

export function useDeviceLogFiles(deviceId: string, filter?: DeviceLogFileFilter, pollingEnabled = false) {
  return useQuery({
    queryKey: logFileKeys.files(deviceId, filter),
    queryFn: () => deviceService.listLogFiles(deviceId, filter),
    enabled: !!deviceId,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: pollingEnabled ? 5_000 : false,
  });
}

export function useLogFileStorageInfo(deviceId: string) {
  return useQuery({
    queryKey: logFileKeys.storageInfo(deviceId),
    queryFn: () => deviceService.getLogFileStorageInfo(deviceId),
    enabled: !!deviceId,
    staleTime: 60_000,
  });
}

export function useForceUploadLog(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deviceService.forceUploadLog(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: logFileKeys.storageInfo(deviceId) });
    },
  });
}

export function useSyncFromGCS(deviceId: string) {
  return useMutation({
    mutationFn: () => deviceService.syncLogFilesFromGCS(deviceId),
  });
}

export function useSyncGCSStatus(deviceId: string, enabled: boolean) {
  return useQuery({
    queryKey: logFileKeys.syncStatus(deviceId),
    queryFn: () => deviceService.getSyncGCSStatus(deviceId),
    enabled: !!deviceId && enabled,
    refetchInterval: enabled ? 2000 : false,
  });
}
